import React, { useState, useRef } from 'react';
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Box, Typography, Paper, Button } from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import postApiCall from "src/server/postApiCall";
import generalUpdateApi from "src/server/generalUpdateApi";
import { useNavigate, useLocation } from "react-router-dom";
import { getGroupTrainingCompletedUrl } from "src/utils/routeUtils";

const FeedBackPop = ({
    visible,
    onHide,
    themes,
    courseId,
    blendedIdcode,
    learningPlanIdcode,
    assignmentIdcode,
    grpTrainingId,
    onSubmit,
    onCancelNavigate
}) => {
    const [comments, setComments] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toastRef = useRef(null);
    const navigate = useNavigate(); // Single declaration of navigate
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const updateGroupTrainingStatus = async (id, status = "completed") => {
        try {
            await generalUpdateApi(`updateGroupTrainingStatus`, {
                groupTrainingId: id,
                status: status,
            });
            if (status === "completed") {
                navigate(getGroupTrainingCompletedUrl(location));
            }
        } catch (error) {
            console.error("Failed to update group training status", error);
        }
    };

    const addUserFeedbackApi = async () => {
        setIsSubmitting(true);

        const payLoad = {
            trainingId: courseId,
            feedback: comments,
            blendedProgramId: blendedIdcode || "",
            learningplanId: learningPlanIdcode || "",
            assignmentId: assignmentIdcode || "",
        };

        try {
            await postApiCall("addUserRating", payLoad);
            if (grpTrainingId && Number(grpTrainingId) > 0) {
                await generalUpdateApi(`updateGroupTrainingStatus`, {
                    groupTrainingId: Number(grpTrainingId),
                    status: "completed",
                });
            }
        } catch (error) {
            console.error("Feedback API Error:", error);
        } finally {
            setIsSubmitting(false);
        }
        onHide();
        if (typeof onSubmit === "function") {
            onSubmit(); 
        } else {
            // Fallback navigation
            if (grpTrainingId && Number(grpTrainingId) > 0) {
                navigate(getGroupTrainingCompletedUrl(location));
            } else {
                navigate(-1);
            }
        }
    };

    const dialogCloseBtnCss = `
    .p-dialog .p-dialog-header .p-dialog-header-icons .p-dialog-header-close {
      width: ${isTablet ? '44px' : '36px'} !important;
      height: ${isTablet ? '44px' : '36px'} !important;
      background-color: transparent !important;
      color: #6c757d !important;
      border-radius: 6px !important;
    }
    .p-dialog .p-dialog-header .p-dialog-header-icons .p-dialog-header-close:hover {
      background-color: rgba(0, 0, 0, 0.05) !important;
    }
  `;

    const handleCancel = async () => {
        onHide();
        if (grpTrainingId && Number(grpTrainingId) > 0) {
            await updateGroupTrainingStatus(Number(grpTrainingId), "completed");
        } else if (typeof onCancelNavigate === "function") {
            onCancelNavigate();
        } else {
            navigate(-1);
        }
    };

    return (
        <>
            <Toast ref={toastRef} />
            <Dialog
                visible={visible}
                style={{ width: isMobile ? "95vw" : "35vw", minWidth: "320px", maxWidth: "650px", maxHeight: "95vh" }}
                breakpoints={{ "960px": "60vw", "641px": "95vw" }}
                modal
                // header="Course Feedback"
                onHide={handleCancel}
            >
                {(isMobile || isTablet) && <style>{dialogCloseBtnCss}</style>}

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 1, pb: 1 }}>
                    <i
                        className="pi pi-check-circle"
                        style={{ fontSize: "3.5rem", color: "#22C55E", marginBottom: "12px" }}
                    />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: "#2C3E50", textAlign: "center" }}>
                        Training Completed!
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#6c757d", textAlign: "center", mb: 3, px: 1, fontSize: isMobile ? "13px" : "15px" }}>
                        Feedback from students helps us improve. We'd love to hear how this training went for you.
                    </Typography>

                    <Paper variant="outlined" sx={{ width: "100%", p: isMobile ? 2 : 3, borderRadius: 2, borderColor: "#e0e0e0", backgroundColor: "#fff" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#343a40" }}>
                            Comments (Optional)
                        </Typography>
                        <textarea
                            className="form-control"
                            placeholder="Share your feedback..."
                            rows={4}
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "4px",
                                border: "1px solid #ced4da",
                                fontSize: isMobile ? "14px" : "15px",
                                marginBottom: "24px",
                                resize: "vertical",
                            }}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                        />
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="contained"
                                onClick={addUserFeedbackApi}
                                disabled={isSubmitting}
                                className={`saveButtonTheme${themes} text-white`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    fontSize: "16px",
                                    padding: "10px 32px",
                                    borderRadius: "6px",
                                    fontWeight: "600",
                                    textTransform: "none",
                                    width: isMobile ? "100%" : "auto",
                                    minWidth: "160px"
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ margin: 0 }} />
                                        <span>Finishing...</span>
                                    </>
                                ) : (
                                    <>
                                        <DoubleArrow sx={{ fontSize: "1.2rem" }} /> Finish
                                    </>
                                )}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Dialog>
        </>
    );
};

export default FeedBackPop;